import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { HALL_BACKGROUNDS, getHallBackgroundColors } from './lib/defaults';
import {
  DialogHeader,
  FieldLabel,
  FP,
  fpDialogActionsSx,
  fpDialogPaperProps,
} from './lib/ui';
import ColorPicker from './ColorPicker';

const HallStyleSheet = ({
  open,
  onClose,
  style,
  onUpdateStyle,
  onSnapshotHistory,
}) => {
  const background = style?.background || 'light';
  const hallColors = getHallBackgroundColors(background);
  const showGrid = style?.showGrid !== false;
  const gridSize = style?.gridSize || 20;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={fpDialogPaperProps}
    >
      <DialogHeader
        title="Floor color"
        subtitle="Pick the floor color of this section."
        onClose={onClose}
      />
      <DialogContent sx={{ px: 3 }}>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          {/* Mirrors how the canvas paints the floor, grid included. */}
          <Box
            sx={{
              height: 76,
              p: 1,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
              borderRadius: 2,
              border: `1px solid ${FP.line}`,
              bgcolor: hallColors.color,
              backgroundImage: showGrid
                ? `linear-gradient(${hallColors.patternColor} 1px, transparent 1px), linear-gradient(90deg, ${hallColors.patternColor} 1px, transparent 1px)`
                : 'none',
              backgroundSize: `${gridSize}px ${gridSize}px`,
              boxShadow: 'inset 0 1px 4px rgba(15,23,42,0.10)',
            }}
          >
            <Typography
              sx={{
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.06em',
                color: FP.ink,
                bgcolor: 'rgba(255,255,255,0.85)',
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
              }}
            >
              {hallColors.color}
            </Typography>
          </Box>

          <Box>
            <FieldLabel>Presets</FieldLabel>
            <Stack
              direction="row"
              spacing={1.25}
              useFlexGap
              sx={{ mt: 1, flexWrap: 'wrap' }}
              alignItems="center"
            >
              {HALL_BACKGROUNDS.map((bg) => {
                const active =
                  background === bg.id || hallColors.color === bg.color;
                return (
                  <Tooltip key={bg.id} title={bg.label}>
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={() => onUpdateStyle({ background: bg.id })}
                      onKeyDown={(e) =>
                        e.key === 'Enter' &&
                        onUpdateStyle({ background: bg.id })
                      }
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        cursor: 'pointer',
                        bgcolor: bg.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: active ? FP.primary : 'transparent',
                        border: active
                          ? `2px solid ${FP.primary}`
                          : `1px solid ${FP.line}`,
                        boxShadow: active
                          ? '0 0 0 3px rgba(45,127,249,0.18)'
                          : 'inset 0 1px 0 rgba(255,255,255,0.5)',
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: active
                            ? '0 0 0 3px rgba(45,127,249,0.18)'
                            : FP.shadowMd,
                        },
                      }}
                    >
                      <CheckIcon fontSize="small" />
                    </Box>
                  </Tooltip>
                );
              })}
            </Stack>
          </Box>

          <Box>
            <FieldLabel>Custom</FieldLabel>
            <Box sx={{ mt: 1 }}>
              <ColorPicker
                value={hallColors.color}
                onDragStart={onSnapshotHistory}
                onChange={(hex) =>
                  onUpdateStyle({ background: hex }, { recordHistory: false })
                }
              />
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={fpDialogActionsSx}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            px: 3,
            bgcolor: FP.primary,
            '&:hover': { bgcolor: FP.primaryDark },
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HallStyleSheet;
